import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "../../../components/DashboardLayout";
import {
  getPastPapersAPI,
  getPastPaperFilesAPI,
  createPastPaperFilesAPI,
  updatePastPaperFilesAPI,
  deletePastPaperFilesAPI,
} from "../../../services/portal/admin/pastPaperService";
import {
  showErrorToastMsg,
  showSuccessToastMsg,
} from "../../../utility/ToastMessage";
import Loader from "../../../components/Loader";
import RemoveConfirmationDialog from "../../../components/portal/RemoveConfirmationDialog";
import { tableHeaderColor } from "../../../utility/Common";
import { RoutePaths } from "../../../utility/RoutePaths";

const MAX_PDF_BYTES = 10 * 1024 * 1024;

const ManagePastPaperFilesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pastPaperId = searchParams.get("pastPaperId");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const [pastPaperDetails, setPastPaperDetails] = useState(null);
  const [formData, setFormData] = useState({ title: "", description: "" });
  const [pdfFile, setPdfFile] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "" });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const loadPastPaperDetails = async () => {
    try {
      const res = await getPastPapersAPI();
      if (res && res.IsSuccessful && res.Data) {
        const item = res.Data.find((p) => String(p.Id) === String(pastPaperId));
        setPastPaperDetails(item || null);
      }
    } catch (error) {
      console.error("Error loading past paper details:", error);
    }
  };

  const loadFiles = useCallback(async () => {
    if (!pastPaperId) return;
    setFiles([]);
    setLoading(true);
    try {
      const res = await getPastPaperFilesAPI(pastPaperId);
      if (res && res.IsSuccessful && Array.isArray(res.Data)) {
        setFiles(res.Data);
      } else {
        setFiles([]);
      }
    } catch (error) {
      console.error("Error loading past paper files:", error);
      showErrorToastMsg("Failed to load files");
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [pastPaperId]);

  useEffect(() => {
    loadPastPaperDetails();
  }, []);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePdfChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") {
      showErrorToastMsg("Please select a PDF file only");
      return;
    }
    if (file.size > MAX_PDF_BYTES) {
      showErrorToastMsg("PDF must be smaller than 10 MB");
      return;
    }
    setPdfFile(file);
  };

  const clearUploadForm = () => {
    setFormData({ title: "", description: "" });
    setPdfFile(null);
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      showErrorToastMsg("Please enter a title");
      return;
    }
    if (!pdfFile) {
      showErrorToastMsg("Please select a PDF file");
      return;
    }
    setSaving(true);
    try {
      const params = new FormData();
      params.append("PastPaperId", String(pastPaperId));
      params.append("Title", formData.title.trim());
      params.append("Description", formData.description.trim());
      params.append("File", pdfFile);
      const res = await createPastPaperFilesAPI(params);
      if (res && res.IsSuccessful) {
        showSuccessToastMsg("File uploaded successfully");
        clearUploadForm();
        await loadFiles();
      } else {
        showErrorToastMsg(res?.Message || "Something went wrong, please try again later");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      showErrorToastMsg("Failed to upload file");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row) => {
    setEditingRow(row);
    setEditForm({ title: row.Title, description: row.Description || "" });
  };

  const cancelEdit = () => {
    setEditingRow(null);
    setEditForm({ title: "", description: "" });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingRow) return;
    if (!editForm.title.trim()) {
      showErrorToastMsg("Please enter a title");
      return;
    }
    setSaving(true);
    try {
      const params = new FormData();
      params.append("Id", Number(editingRow.Id));
      params.append("Title", editForm.title.trim());
      params.append("Description", editForm.description.trim());
      const res = await updatePastPaperFilesAPI(params);
      if (res && res.IsSuccessful) {
        showSuccessToastMsg("File updated successfully");
        cancelEdit();
        await loadFiles();
      } else {
        showErrorToastMsg(res?.Message || "Something went wrong, please try again later");
      }
    } catch (error) {
      console.error("Error updating file:", error);
      showErrorToastMsg("Failed to update file");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (row) => {
    setDeleteItem(row);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    setSaving(true);
    try {
      const res = await deletePastPaperFilesAPI(deleteItem.Id);
      if (res && res.IsSuccessful) {
        showSuccessToastMsg("File removed successfully");
        if (editingRow && editingRow.Id === deleteItem.Id) cancelEdit();
        await loadFiles();
      } else {
        showErrorToastMsg("Failed to remove file");
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      showErrorToastMsg("Failed to remove file");
    } finally {
      setSaving(false);
      setShowDeleteConfirm(false);
      setDeleteItem(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteItem(null);
  };

  return (
    <DashboardLayout
      title="Past Paper Files"
      subtitle="Upload PDFs linked to this past paper"
    >
      <div className="mb-4">
        <button
          type="button"
          onClick={() => navigate(RoutePaths.ADMIN_MANAGE_PAST_PAPERS_PAGE)}
          className="text-sm font-medium text-primary-600 hover:text-primary-800"
        >
          ← Back to past papers
        </button>
      </div>

      {pastPaperDetails && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6 border-l-4 border-primary-600">
          <h2 className="text-xl font-bold text-gray-900">
            {pastPaperDetails.Title}
          </h2>
          {pastPaperDetails.Description && (
            <p className="text-sm text-gray-600 mt-1">{pastPaperDetails.Description}</p>
          )}
        </div>
      )}

      {loading ? (
        <Loader />
      ) : (
        <div className="space-y-8">
          {/* Upload / Edit Form */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}>
              <h4 className="text-md font-medium text-white">
                {editingRow ? "Edit file" : "Upload file"}
              </h4>
            </div>
            <div className="p-6">
              {editingRow ? (
                <form onSubmit={handleEditSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={editForm.title}
                      onChange={handleEditChange}
                      className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={editForm.description}
                      onChange={handleEditChange}
                      rows={4}
                      className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex flex-wrap gap-3 pt-2">
                    <button
                      type="submit"
                      disabled={saving}
                      className={`text-md font-medium px-4 py-2 rounded-md transition-colors ${
                        saving
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-primary-600 text-white hover:bg-primary-700"
                      }`}
                    >
                      {saving ? "Saving..." : "Save changes"}
                    </button>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      disabled={saving}
                      className="text-md font-medium px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleUploadSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="File title"
                      className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Optional description"
                      className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      PDF file <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      onChange={handlePdfChange}
                      className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">PDF only, maximum size 10 MB.</p>
                    {pdfFile && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs text-gray-600">Selected: {pdfFile.name}</span>
                        <button
                          type="button"
                          onClick={() => setPdfFile(null)}
                          className="text-xs text-red-600 hover:text-red-800 font-medium"
                        >
                          Clear
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={saving}
                      className={`text-md font-medium px-4 py-2 rounded-md transition-colors ${
                        saving
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-primary-600 text-white hover:bg-primary-700"
                      }`}
                    >
                      {saving ? "Uploading..." : "Upload"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Files Table */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}>
              <h4 className="text-md font-medium text-white">Uploaded files</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      File
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {files.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">
                        No files yet. Upload a PDF above.
                      </td>
                    </tr>
                  ) : (
                    files.map((row) => (
                      <tr key={row.Id}>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                          {row.Title}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 max-w-md">
                          <span className="line-clamp-3 whitespace-pre-wrap">
                            {row.Description}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {row.Path ? (
                            <a
                              href={row.Path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:text-primary-800 font-medium"
                            >
                              View PDF
                            </a>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => startEdit(row)}
                            className="mr-4 text-primary-600 hover:text-primary-900"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(row)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <RemoveConfirmationDialog
          loading={saving}
          description={deleteItem ? deleteItem.Title : ""}
          cancelDelete={cancelDelete}
          confirmDelete={confirmDelete}
        />
      )}
    </DashboardLayout>
  );
};

export default ManagePastPaperFilesPage;
